<template>
    <v-form
        v-if="form"
        class="product-form"
    >
        <template v-if="step == 1">
            <v-row class="mt-0">
                <v-col cols="12" sm="9">
                    <v-text-field
                        v-model="form.name"
                        :error-messages="form.$errors.name"
                        :label="$t('general.name')"
                        @blur="form.validate('name')"
                        @input="delete form.$errors.name"
                    />
                </v-col>

                <v-col cols="12" sm="3">
                    <v-text-field
                        v-model="form.code"
                        :error-messages="form.$errors.code"
                        :label="$t('general.code')"
                        @blur="form.validate('code')"
                        @input="delete form.$errors.code"
                    />
                </v-col>
            </v-row>

            <v-btn-toggle
                v-model="form.is_goods"
                class="mt-2 mb-1 wfull d-flex"
                mandatory
                dense
            >
                <v-btn
                    class="flex-1"
                    color="primary"
                    text
                    :value="true"
                >
                    {{ $t('general.goods') }}
                </v-btn>
                <v-btn
                    class="flex-1"
                    color="primary"
                    text
                    :value="false"
                    :disabled="mode === 'inventoryAccounting'"
                >
                    {{ $t('general.service') }}
                </v-btn>
            </v-btn-toggle>

            <v-row v-if="form.is_goods" class="mt-0">
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="form.barcode"
                        :error-messages="form.$errors.barcode"
                        :label="$t('general.barcode')"
                        @blur="form.validate('barcode')"
                        @input="delete form.$errors.barcode"
                    />
                </v-col>

                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="form.gtin"
                        :error-messages="form.$errors.gtin"
                        :label="$t('general.gtin_short')"
                        @blur="form.validate('gtin')"
                        @input="delete form.$errors.gtin"
                    />
                </v-col>
            </v-row>

            <v-row class="mt-0">
                <v-col cols="12" :sm="!isAllegra ? 6 : 12">
                    <v-combobox
                        v-model="category"
                        ref="categoryField"
                        :error-messages="categoryForm.$errors.name"
                        :label="$t('general.category')"
                        :search-input.sync="categorySearch"
                        :items="productCategories"
                        item-text="name"
                        persistent-hint
                        :hint="categoryForm.name && !categoryForm.id ? $t('products.new_category_message') : null"
                        clearable
                        clear-icon="fal fa-times"
                        @blur="categoryForm.validate('name')"
                        @input="delete categoryForm.$errors.name"
                        @change="onCategoryChange"
                    >
                        <template v-if="categorySearch" v-slot:no-data>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title v-html="$t('products.no_category_message', { value: categorySearch })" />
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                    </v-combobox>
                </v-col>

                <v-col v-if="!isAllegra" cols="12" sm="3">
                    <v-select
                        v-model="categoryForm.type"
                        :items="appConfig.product.types"
                        :label="$t('general.type')"
                        :error-messages="categoryForm.$errors.type"
                        :disabled="!categoryForm.name || !!categoryForm.id"
                        @change="categoryForm.validate('type')"
                    >
                        <template
                            slot="selection"
                            slot-scope="data"
                        >
                            <label>{{ $t(`general.${data.item}`) }}</label>
                        </template>
                        <template
                            slot="item"
                            slot-scope="data"
                        >
                            <label>{{ $t(`general.${data.item}`) }}</label>
                        </template>
                    </v-select>
                </v-col>

                <v-col v-if="!isAllegra" cols="12" sm="3">
                    <v-select
                        v-model="form.printing_sector"
                        :items="printingSectors"
                        item-value="key"
                        item-text="value"
                        :label="$t('products.print_to')"
                        :error-messages="form.$errors.printing_sector"
                        @change="form.validate('printing_sector')"
                    />
                </v-col>
            </v-row>

            <v-row v-if="form.is_goods" class="grey lighten-4 my-4">
                <v-col v-if="isAllegra" class="py-0" cols="12">
                    <v-checkbox
                        v-model="form.is_measured"
                        :error-messages="form.$errors.is_measured"
                    >
                        <div slot="label">
                            <div>{{ $t('products.is_measured') }}</div>
                            <small>{{ $t('products.is_measured_description') }}</small>
                        </div>
                    </v-checkbox>
                </v-col>

                <v-col v-if="!isAllegra" class="py-0" cols="12">
                    <v-checkbox
                        v-model="form.is_ingredient"
                        :error-messages="form.$errors.is_ingredient"
                        :disabled="mode === 'inventoryAccounting'"
                    >
                        <div slot="label">
                            <div>{{ $t('products.is_ingredient') }}</div>
                            <small>{{ $t('products.is_ingredient_description') }}</small>
                        </div>
                    </v-checkbox>
                </v-col>

                <v-col v-if="!isAllegra" class="py-0" cols="12">
                    <v-divider />
                </v-col>

                <v-col v-if="!isAllegra" class="py-0" cols="12">
                    <v-checkbox
                        v-model="form.is_made"
                        :error-messages="form.$errors.is_made"
                        :disabled="mode === 'inventoryAccounting'"
                    >
                        <div slot="label">
                            <div>{{ $t('products.is_made') }}</div>
                            <small>{{ $t('products.is_made_description') }}</small>
                        </div>
                    </v-checkbox>
                </v-col>
            </v-row>

            <v-row class="d-flex">
                <!-- Selling Price Field -->
                <v-col :cols="!isAllegra ? 2 : 6" class="flex-grow-1">
                    <number-field
                        v-model="form.selling_price"
                        :label="priceLabel"
                        :error-messages="form.$errors.selling_price"
                        :disabled="disableSellingPriceEdit"
                        :precision="3"
                        @blur="form.validate('selling_price')"
                        @input="delete form.$errors.selling_price"
                    />
                </v-col>

                <!-- Tax Rate Field -->
                <v-col :cols="!isAllegra ? 2 : 6" class="flex-grow-1">
                    <v-select
                        v-model="form.tax_rate_id"
                        :error-messages="form.$errors.tax_rate_id"
                        :label="$t('general.tax_rate')"
                        :items="availableTaxRates"
                        item-text="display"
                        item-value="id"
                        @change="form.validate('tax_rate_id')"
                    />
                </v-col>

                <!-- In Stock Fields (Only visible if conditions match) -->
                <template v-if="!tenant.venue.settings.hide_product_quantities && !form.is_made && form.is_goods">
                    <v-col :cols="2" class="flex-grow-1">
                        <number-field
                            v-model="form.count"
                            :error-messages="form.$errors.count"
                            :label="$t('general.in_stock')"
                            :precision="3"
                            :disabled="inventoryAccountingEnabled"
                            @blur="form.validate('count')"
                            @input="delete form.$errors.count"
                        />
                    </v-col>

                    <v-col :cols="2" class="flex-grow-1">
                        <number-field
                            v-model="form.min_count"
                            :error-messages="form.$errors.min_count"
                            :label="$t('general.min_stock')"
                            :precision="3"
                            @blur="form.validate('min_count')"
                            @input="delete form.$errors.min_count"
                        />
                    </v-col>
                </template>

                <!-- Unit of Measure Field -->
                <v-col :cols="2" class="flex-grow-1">
                    <v-combobox
                        v-model="form.unit_of_measure"
                        :error-messages="form.$errors.unit_of_measure"
                        :label="$t('products.unit')"
                        :items="unitsOfMeasure"
                        item-value="key"
                        item-text="value"
                        @blur="form.validate('unit_of_measure')"
                        @input="delete form.$errors.unit_of_measure"
                    />
                </v-col>

                <!-- Unit of Measure Sold Field (If visible) -->
                <v-col v-if="showUnitOfMeasureSold" :cols="2" class="flex-grow-1">
                    <v-select
                        v-model="form.unit_of_measure_sold"
                        :error-messages="form.$errors.unit_of_measure_sold"
                        :label="$t('products.unit_sold')"
                        :items="unitsOfMeasure"
                        item-value="key"
                        item-text="value"
                        @blur="form.validate('unit_of_measure_sold')"
                        @input="delete form.$errors.unit_of_measure_sold"
                    />
                </v-col>

                <!-- Net Quantity Field (If visible) -->
                <v-col v-if="showNetQuantity" :cols="2" class="flex-grow-1">
                    <number-field
                        v-model="netQuantity"
                        :error-messages="form.$errors.net_quantity"
                        :label="$t('general.net_quantity_in', { value: netQuantityUnitOfMeasure })"
                        :precision="3"
                        @blur="form.validate('net_quantity')"
                        @input="delete form.$errors.net_quantity"
                    />
                </v-col>
            </v-row>

            <v-row v-if="form.prices.length" class="mb-1">
                <v-col
                    v-for="(pricelist, index) in form.prices"
                    :key="index"
                    cols="6"
                    sm="3"
                >
                    <number-field
                        v-model="pricelist.price"
                        :label="`${pricelist.name} ${$t('general.price')}`"
                        :error-messages="form.$errors[`prices.${index}.price`]"
                        :precision="3"
                        :disabled="disableSellingPriceEdit"
                        @blur="form.validate(`prices.${index}.price`)"
                        @input="delete form.$errors[`prices.${index}.price`]"
                    />
                </v-col>
            </v-row>
        </template>

        <template v-if="step == 2">
            <div class="pt-3">
                <v-alert
                    icon="fal fa-exclamation-circle"
                    color="primary"
                    prominent
                    text
                >
                    {{ $t('products.no_quantity') }}
                </v-alert>
            </div>

            <v-autocomplete
                v-model="ingredientIdToAdd"
                :items="availableIngredients"
                item-text="name"
                item-value="id"
                :label="$tc('products.select_ingredient', 1)"
                autofocus
            />

            <v-row no-gutters>
                <v-col>
                    <number-field
                        v-model="ingredientQuantity"
                        :label="$t('general.quantity')"
                        :precision="3"
                        class="pr-1"
                        :disabled="!ingredientIdToAdd"
                    />
                </v-col>

                <v-col>
                    <number-field
                        v-model="ingredientQuantityMultiplier"
                        :label="$t('general.multiplied_by')"
                        :precision="3"
                        prefix="x"
                        class="pl-1"
                        :disabled="!ingredientIdToAdd"
                    />
                </v-col>

                <v-col
                    class="flex align-items-center"
                    style="word-break: break-all"
                >
                    <span v-if="ingredientToAdd && ingredientQuantityMultiplier && ingredientQuantity">
                        {{ `= ${(ingredientQuantityMultiplier * ingredientQuantity).toFixed(3)} ${ingredientToAdd.unit_of_measure}` }}
                    </span>
                </v-col>

                <v-col class="flex align-items-center">
                    <v-spacer />
                    <v-btn
                        v-if="ingredientToAdd && ingredientQuantity && ingredientQuantityMultiplier"
                        color="primary"
                        text
                        class="minw100 mb-1"
                        @click="addIngredient"
                    >
                        <v-icon left>fal fa-plus</v-icon> {{ $t('general.add') }}
                    </v-btn>
                </v-col>
            </v-row>

            <div class="pt-3 pb-2 primary--text">{{ $tc('general.ingredient', 2) }}</div>

            <v-divider />

            <v-data-table
                class="ingredients-table"
                :items="ingredients"
                :items-per-page="100"
                :mobile-breakpoint="0"
                hide-default-header
                hide-default-footer
                dense
            >
                <template
                    slot="item"
                    slot-scope="props"
                >
                    <tr>
                        <td>{{ props.item.name }}</td>
                        <td>{{ props.item.pivot.quantity + ' ' + props.item.unit_of_measure }}</td>
                        <td align="right">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <span
                                        class="cursor-pointer px-1"
                                        @click="deleteIngredient(props.item)"
                                        v-on="on"
                                    >
                                        <i
                                            class="fal fa-times error--text"
                                            style="font-size: 20px;"
                                        />
                                    </span>
                                </template>
                                <span>{{ $t('general.delete') }}</span>
                            </v-tooltip>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </template>

        <div class="form-buttons">
            <template v-if="step == 1">
                <v-btn
                    v-if="form.is_made"
                    class="flex-1 mx-1"
                    color="primary"
                    outlined
                    @click="goToIngredients"
                >
                    {{ $t('products.go_to_ingredients') }}
                </v-btn>

                <v-btn
                    v-else
                    class="flex-1 mx-1"
                    color="primary"
                    @click="save"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </template>

            <template v-if="step == 2">
                <v-btn
                    class="flex-1 mr-1"
                    outlined
                    depressed
                    color="primary"
                    :disabled="form.busy"
                    @click="step = 1"
                >
                    {{ $t('general.back') }}
                </v-btn>

                <v-btn
                    class="flex-1 ml-1"
                    color="primary"
                    :loading="form.$busy"
                    :disabled="form.$busy || !ingredients.length"
                    @click="save"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </template>
        </div>
    </v-form>
</template>

<script>
import Product from '@/models/Product'
import Pricelist from '@/models/Pricelist'
import ProductCategory from '@/models/ProductCategory'
import TaxRate from '@/models/TaxRate'
import ApiService from '@/services/ApiService'
import NumberField from '@/components/NumberField';
import Money from '@/wrappers/Money';
import { required, requiredIf, minLength, maxLength, minValue } from 'vuelidate/lib/validators'

export default {
    components: {
        NumberField
    },

    props: {
        mode: {
            type: String,
            default: 'default'
        },
        product: {
            type: Object,
            default: () => null
        },
        pricelists: {
            type: Array,
            default: () => Pricelist.all()
        },
        products: {
            type: Array,
            default: () => Product.all()
        },
        productCategories: {
            type: Array,
            default: () => ProductCategory.all()
        },
    },

    created() {
        this.form = this.createForm(this.product);
        this.categoryForm = this.createCategoryForm(this.product);
    },

    data() {
        return {
            form: null,
            netQuantity: null,

            categoryForm: null,
            categorySearch: null,
            category: null,

            ingredients: [],
            ingredientIdToAdd: null,
            ingredientQuantity: 1,
            ingredientQuantityMultiplier: 1,

            step: 1,
        };
    },

    validations() {
        const priceRules = {
            requiredIf: requiredIf(() => this.form.is_sold && !this.inventoryAccountingEnabled),
            minValue: minValue(!this.inventoryAccountingEnabled ? 0.01 : 0)
        };

        return {
            form: {
                name: { required: required, maxLength: maxLength(200) },
                code: { requiredIf: requiredIf(() => this.form.is_measured) },
                gtin: { minLength: minLength(8), maxLength: maxLength(14) },
                type: { requiredIf: requiredIf(() => !isAllegra) },
                printing_sector: { requiredIf: requiredIf(() => !isAllegra) },
                selling_price: priceRules,
                selling_count: { requiredIf: requiredIf(() => this.form.is_sold && !this.form.is_made) },
                count: { requiredIf: requiredIf(() => !this.form.is_made && !this.inventoryAccountingEnabled) },
                min_count: {
                    minValue: (value) => {
                        if(value === null || value === 0 || value === '' || isNaN(value)) {
                            return true;
                        }

                        return minValue(0)(value);
                    }
                },
                unit_of_measure: { required: required },
                unit_of_measure_sold: { required: required },
                tax_rate_id: { required: required },
                prices: {
                    $each: {
                        price: { minValue: priceRules.minValue }
                    }
                }
            },

            categoryForm: {
                name: { requiredIf: requiredIf(() => !isAllegra) },
                type: { requiredIf: requiredIf(() => !isAllegra) },
            }
        }
    },

    watch: {
        product(val) {
            this.form = this.createForm(val);
            this.categoryForm = this.createCategoryForm(val);
        },

        'form.is_goods'(val) {
            if(!val) {
                this.form.barcode = null;
                this.form.gtin = null;
                this.form.is_measured = false;
                this.form.is_ingredient = false;
                this.form.is_made = false;
                this.form.selling_price = null;
                this.form.prices.forEach(p => (p.price = null));
            }

            this.form.validate();
        },

        'form.is_ingredient'(val) {
            this.form.is_sold = !val;

            if(this.form.is_ingredient) {
                this.form.is_made = false;
                this.form.selling_price = null;
                this.form.prices.forEach(p => (p.price = null));
            } else {
                const defaultPrice = this.inventoryAccountingEnabled ? 0 : null;
                this.form.selling_price = parseFloat(this.product?.selling_price) || defaultPrice;
                this.form.prices.forEach(p => {
                    const existingPrice = this.product?.prices.find(price => price.id === p.id) || null;
                    p.price = existingPrice?.price || null;
                })
            }

            this.form.selling_count = this.form.is_ingredient || this.form.is_made ? null : 1;
            this.form.validate();
        },

        'form.is_made'(val) {
            if (this.form.is_made) {
                this.form.is_ingredient = false;
                this.form.count = null;
            } else {
                this.form.count = parseFloat(this.product?.count) || 0;
            }

            this.form.selling_count = this.form.is_ingredient || this.form.is_made ? null : 1;
            this.form.validate();
        },

        'form.unit_of_measure'(val, oldVal) {
            if(!val || !['kg', 'l'].includes(val?.key || val)) {
                this.form.net_quantity = null;
                this.netQuantity = null;
            }

            if(!oldVal) return

            this.form.unit_of_measure_sold = val?.key || val
        },

        'categoryForm.type'(val) {
            this.form.type = val;
        },
    },

    computed: {
        availableIngredients() {
            return this.products.filter(product => {
                return !this.ingredients.some(i => i.id == product.id)
                    && product.id !== this.form.id
                    && !product.is_made
                    && !product.is_sold;
            });
        },

        availableTaxRates() {
            let taxRates = TaxRate.productTaxRates()

            taxRates.forEach(taxRate => {
                taxRate.display = taxRate.name
                    ? `${taxRate.name} (${taxRate.label}) - ${taxRate.rate}%`
                    : `${taxRate.label} - ${taxRate.rate}%`
            });

            return taxRates;
        },

        ingredientToAdd() {
            if (!this.ingredientIdToAdd) {
                return null;
            }

            let ingredient = this.products.find(product => product.id === this.ingredientIdToAdd);
            ingredient.pivot = { product_id: ingredient.id, quantity: null };
            return ingredient;
        },

        unitsOfMeasure() {
            const predefinedUnits = this.appConfig.product.unitsOfMeasure
            const selectedUnits = this.products.map(p => p.unit_of_measure)
            const allUnits = [...predefinedUnits, ...selectedUnits].filter((unit, index, array) => index === array.indexOf(unit))

            return allUnits.map(unit => {
                return {
                    key: unit,
                    value: this.$t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? this.$t(`products.unit_of_measure.${unit}`)
                        : unit
                }
            })
        },

        printingSectors() {
            return this.appConfig.product.printingSectors.map(printSector => {
                return {
                    key: printSector,
                    value: this.$t(`products.printing_sector.${printSector}`)
                }
            })
        },

        priceLabel() {
            const label = this.$t('general.price')
            const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation
            const suffix = reverseCalculation ? this.$t('general.pre_tax') : this.$t('general.after_tax')
            return label + ' ' + suffix.charAt(0).toLowerCase() + suffix.slice(1);
        },

        inventoryAccountingEnabled() {
            if(!this.form?.is_goods || this.form?.is_made || !this.form?.is_sold) return false;
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        },

        inventoryAccountingPriceChangeEnabled() {
            return !!this.tenant.venue?.settings?.inventory_accounting_price_change_enabled;
        },

        disableSellingPriceEdit() {
            return !this.form.is_sold || (this.inventoryAccountingEnabled && !(this.form.id && this.inventoryAccountingPriceChangeEnabled))
        },

        enablePricelists() {
            return !this.tenant.venue?.settings?.use_inventory_accounting && !isAllegra;
        },

        showNetQuantity() {
            if(!isAllegra) return false;
            if(!this.unitOfMeasureLabel) return false;

            return ['kg', 'l'].includes(this.unitOfMeasureLabel.toLowerCase())
        },

        showUnitOfMeasureSold() {
            if(locale !== 'sr') return false;
            if(!isAllegra) return false;
            if(!this.unitOfMeasureLabel) return false;

            return !['pcs', this.$t('products.unit_of_measure.pcs')].includes(this.unitOfMeasureLabel.toLowerCase())
        },

        unitOfMeasureLabel() {
            if(!this.form.unit_of_measure) return '';
            return typeof this.form.unit_of_measure === 'object' && this.form.unit_of_measure !== null
                ? this.form.unit_of_measure.key
                : this.form.unit_of_measure;
        },

        netQuantityUnitOfMeasure() {
            if(this.unitOfMeasureLabel?.toLowerCase() === 'kg') return 'g'
            if(this.unitOfMeasureLabel?.toLowerCase() === 'l') return 'ml'
            return ''
        }
    },

    methods: {
        /**
         * Create product form
         */
        createForm(product = null) {
            let codes = [];
            if(this.products) {
                codes = this.products.filter(p => {
                    return !isNaN(p.code) && parseInt(Number(p.code)) == p.code && !isNaN(parseInt(p.code, 10));
                }).map(p => p.code)
            }

            const nextCode = codes.length ? ('' + (Math.max(...codes) + 1)) : null;
            const defaultPrice = this.inventoryAccountingEnabled ? 0 : null;
            const defaultTaxRateId = TaxRate.productTaxRates().length === 1 ? TaxRate.productTaxRates()[0]?.id : null

            this.ingredients = product && product.ingredients ? JSON.parse(JSON.stringify(product.ingredients)) : [];
            this.netQuantity = product && product.net_quantity ? (parseFloat(product.net_quantity) * 1000) : null;

            return new Form(this, 'form', {
                id: product ? product.id : null,
                name: product ? product.name : null,
                code: product ? product.code : nextCode,
                barcode: product ? product.barcode : null,
                gtin: product ? product.gtin : null,
                is_goods: product ? (product.is_goods === null || !!product.is_goods) : true,
                is_measured: product ? product.is_measured : false,
                is_sold: product ? product.is_sold : true,
                is_made: product ? product.is_made : false,
                selling_price: product && product.selling_price != null ? parseFloat(product.selling_price) : defaultPrice,
                tax_rate_id: product ? product.tax_rate_id : defaultTaxRateId || null,
                type: product ? product.type : null,
                category_id: product ? product.category_id : null,
                printing_sector: product ? product.printing_sector : (this.appConfig && !isAllegra ? this.appConfig.product.printingSectors[0] : null),
                selling_count: product ? parseFloat(product.selling_count) : 1,
                count: product ? parseFloat(product.count) : 0,
                min_count: product ? parseFloat(product.min_count) : null,
                unit_of_measure: product ? product.getUnitOfMeasure() : this.unitsOfMeasure[0].value,
                unit_of_measure_sold: product ? product.getUnitOfMeasureSold() : this.unitsOfMeasure[0].value,
                net_quantity: product && product.net_quantity ? parseFloat(product.net_quantity) : null,
                prices: !this.enablePricelists ? [] : this.pricelists.map(p => {
                    const existingPrice = product?.prices.find(price => price.id === p.id) || null;
                    return { id: p.id, name: p.name, price: existingPrice?.price || null };
                }),
                is_ingredient: product ? !product.is_sold : false,
            })
        },

        /**
         * Create category form
         */
        createCategoryForm(product) {
            let category = product?.category || null;
            if(!category && product?.category_id) {
                category = this.productCategories.find(c => c.id == product.category_id)
            }

            this.category = category;

            return new Form(this, 'categoryForm', {
                id: category?.id || null,
                name: category?.name || null,
                type: category?.type || null,
            })
        },

        /**
         * On category change update the product and category form depending on
         * whether the existing category is selected or new one created
         */
        onCategoryChange(event) {
            this.$nextTick(() => {
                this.$refs.categoryField.blur();
            })

            if(!this.category) {
                this.form.category_id = null;
                this.form.type = null;

                this.categoryForm = this.createCategoryForm();
                this.categoryForm.validate();
                return;
            }

            const newCategory = typeof this.category === 'string' || this.category instanceof String;

            // Check if we're in invalid state (existing category selected then deleted via delete btn on keyboard)
            if(newCategory) {
                const existingCategory = this.productCategories.find(c => c.name === this.category);
                if(existingCategory) {
                    this.category = null;
                    this.form.category_id = null;
                    this.form.type = null;

                    this.categoryForm = this.createCategoryForm();
                    this.categoryForm.validate();
                    return;
                }
            }

            this.form.category_id = newCategory ? null : this.category.id;
            this.form.type = newCategory ? null : this.category.type;

            this.categoryForm.id = newCategory ? null : this.category.id;
            this.categoryForm.type = newCategory ? null : this.category.type;
            this.categoryForm.name = newCategory ? this.category : this.category.name;
            this.categoryForm.validate();

            if (!newCategory) {
                if (this.category.type === 'food') {
                    this.form.printing_sector = Product.PRINTING_SECTOR_KITCHEN;
                } else if (this.category.type === 'drinks') {
                    this.form.printing_sector = Product.PRINTING_SECTOR_BAR;
                }
            }
        },

        /**
         * Go to ingredients step
         */
        goToIngredients() {
            const formValid = this.form.validate();
            const categoryFormValid = this.categoryForm.validate();
            if (!formValid || !categoryFormValid) return;

            this.step = 2;
        },

        /**
         * Add ingredient to the list of product ingredients
         */
        addIngredient() {
            this.ingredientToAdd.pivot.quantity = this.ingredientQuantityMultiplier * this.ingredientQuantity;
            this.ingredients.push(this.ingredientToAdd);
            this.ingredientIdToAdd = null;
            this.ingredientQuantity = 1;
            this.ingredientQuantityMultiplier = 1;
        },

        /**
         * Delete ingredient from the list of product ingredients
         */
        deleteIngredient(product) {
            this.ingredients = this.ingredients.filter(item => item.id !== product.id);
        },

        /**
         * Save Product
         */
        async save() {
            const formValid = this.form.validate();
            const categoryFormValid = this.categoryForm.validate();
            if (!formValid || !categoryFormValid) return;

            if(!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return;
            }

            this.form.unit_of_measure = typeof this.form.unit_of_measure === 'object' && this.form.unit_of_measure !== null
                ? this.form.unit_of_measure.key
                : this.form.unit_of_measure

            if(!this.form.unit_of_measure_sold) {
                this.form.unit_of_measure_sold = this.form.unit_of_measure
            }

            this.form.ingredients = this.step == 2 ? this.ingredients.map(i => i.pivot).flat() : [];

            const newCategory = typeof this.category === 'string' || this.category instanceof String;
            this.form.category = newCategory ? this.categoryForm.toJson() : null;

            if (!this.form.is_ingredient) {
                const usedAsIngredient = this.products.filter(p => !!p.ingredients)
                    .map(p => p.ingredients).flat()
                    .map(i => i.pivot).flat()
                    .find(i => i.product_id === this.product?.id);

                if (usedAsIngredient) {
                    this.$snackbar(this.$t('products.has_super_products'), 'error', { mode: 'multi-line' });
                    return;
                }
            }

            this.form.net_quantity = this.netQuantity ? Money.parse(this.netQuantity).divide(1000).toFormat('0.000') : null;

            const response = !this.form.id
                ? await ApiService.post('/admin/products', this.form)
                : await ApiService.patch(`/admin/products/${this.form.id}`, this.form)

            Product.insert({ data: [response.data] });
            if(response.data.category) {
                ProductCategory.insert({ data: [response.data.category] });
            }

            const product = new Product(response.data);
            product.category = response.data.category ? new ProductCategory(response.data.category) : null;
            product.tax_rate = response.data.tax_rate ? new TaxRate(response.data.tax_rate) : null;
            product.ingredients = response.data.ingredients;

            this.$emit('save', {
                product: product,
                category: product.category
            });

            this.form = this.createForm();
            this.categoryForm = this.createCategoryForm();

            this.$snackbar(this.$t('products.inventory_item_saved'), 'success');
        },
    }
};
</script>

<style lang="scss" scoped>
.product-form {
    min-height: 645px;
    padding: 0 12px 60px 12px;
    position: relative;

    .form-buttons {
        display: flex;
        justify-content: center;
        position: absolute;
        left: 16px;
        right: 16px;
        bottom: 12px;
    }

    .ingredients-table {
        max-height: 256px;
        overflow-y: auto;
    }
}
</style>
